import { ref } from 'vue'
import { updateProfile } from '@/services/auth'
import { useAdminDB } from '@/composables/useAdminDB'

export type AvailValue = 'online' | 'busy' | 'offline'

export const AVAIL_OPTIONS: { value: AvailValue; label: string; color: string }[] = [
  { value: 'online',  label: 'Online',  color: '#22c55e' },
  { value: 'busy',    label: 'Ocupado', color: '#f59e0b' },
  { value: 'offline', label: 'Offline', color: '#9ca3af' },
]

const _status = ref<AvailValue>((localStorage.getItem('user_avail') as AvailValue) ?? 'offline')

export const availStatus = _status

// Sincroniza status entre abas: alterar a disponibilidade em uma aba (ex.: ConvDrawer
// ou DashHeader) reflete nas demais sem reload — o evento só dispara nas abas que
// não fizeram a escrita.
window.addEventListener('storage', (e) => {
  if (e.key === 'user_avail' && e.newValue) {
    _status.value = (e.newValue as AvailValue) || 'offline'
  }
})

export function syncAvailStatus(status: string) {
  _status.value = (status as AvailValue) || 'offline'
  localStorage.setItem('user_avail', _status.value)
}

// Usa o endpoint de perfil (PUT /api/v1/profile) — não o de agentes (PUT /agents/:id),
// que no Chatwoot é restrito a administradores e retornava 401 para agente/supervisor
// tentando mudar o próprio status.
//
// Não usamos `availability_status` da resposta pra confirmar o valor: esse campo
// é a presença computada pelo Chatwoot (via ActionCable/auto_offline), separada
// do campo `availability` que acabamos de gravar, e costuma ficar "offline" pra
// quem só acessa via esta dashboard (sem websocket de presença como o Chatwoot
// nativo mantém) — usá-la aqui fazia o status voltar sozinho pra offline. Como o
// PUT deu certo, `status` (o valor que pedimos) já É o valor aplicado.
//
// Também grava o status na nossa própria tabela (agent_status, migração v14):
// é o que permite outras telas (Últimos Acessos, aba Agentes) mostrarem o
// status de QUALQUER agente corretamente — o Chatwoot não expõe o campo
// `availability` de terceiros na listagem de agentes, só o `availability_status`
// computado (que fica travado em offline pra quem só acessa via esta dashboard).
export async function updateAvailability(token: string, status: AvailValue, userId: number): Promise<void> {
  _status.value = status
  localStorage.setItem('user_avail', status)

  // Dispara os dois em paralelo — se o PUT /profile do Chatwoot falhar (ex.:
  // flakiness pontual da API), o gravar em agent_status não pode ficar
  // pendurado nisso: é a fonte que Últimos Acessos/aba Agentes usam pro status
  // de QUALQUER agente, e antes disso ficava "presa" em offline pra sempre
  // (ex.: depois de um logout forçado pelo admin) até o agente reabrir o
  // dropdown de disponibilidade manualmente.
  const [profileResult, dbResult] = await Promise.allSettled([
    updateProfile(token, { availability: status }),
    useAdminDB().updateAgentStatus(userId, status),
  ])

  if (dbResult.status === 'rejected') {
    console.warn('[Availability] updateAgentStatus:', dbResult.reason)
  }
  if (profileResult.status === 'rejected') throw profileResult.reason
}
